/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef, useState} from 'react';

/**
 * A hook that provides the ability to combine both state and reference.
 * This can be very useful to refer to the latest value within the method
 * that is created by debounce.
 *
 * ```typescript
 *
 *  import {useStateRef} from '@ion/dls/src/hooks/';
 *
 *  export const SomeComponent = (props: any) => {
 *    const [state, setState, ref] = useStateRef(0);
 *
 *    const handleOnChange = useCallback(debounce((e) => {
 *      e.preventDefault();
 *      const {target: {value}} = target;
 *      setState(value);
 *    }, 1000), []);
 *
 *   return (
 *    <TextInput value={state} onChange={handleOnChange} />
 *   );
 *  };
 *
 * ```
 */
export const useStateRef = (initialState?: any) => {
  const [state, _setState] = useState(initialState);
  const ref                = useRef(initialState);

  const setState = useCallback((value: any) => {
    _setState(value);
    ref.current = value;
  }, []);

  return [state, setState, ref];
};

export default useStateRef;
